import React, { useContext, useState } from 'react'
import { orderBy } from 'lodash'
import { Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { ColorMarker, GridTableStatusEnum, IGridTableColumn, NewGridTable } from '@ui'
import { IPortfolioDepartment, Order } from '@api'
import { TabContext, PrintTitle } from '../Details'
import { SimpleBar } from './SeatedEmployeesByDepartment'
import { ITooltipByAllocatedInDepartments } from '../hooks/usePortfolioTooltips'
import classnames from 'classnames'
import { pluralize } from '@utils'

const occupiedColor = '#408BE6'
const unoccupiedColor = '#A6DFFC'

type Sorts = 'department.name' | 'allocated' | 'occupied' | 'unoccupied'

export default function SeatsByDepartment() {
  const [sort, setSort] = useState<Sorts>('allocated')
  const [sortAscending, setSortAscending] = useState(false)
  const { object, loading } = useContext(TabContext)
  const departments = object?.departments || []

  const max = departments.reduce((max, d) => Math.max(max, d.occupied + d.unoccupied), 0)

  const columns: IGridTableColumn<IPortfolioDepartment & { id: string }>[] = [
    {
      label: 'Department',
      sort: 'department.name',
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="department" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.department?.name : 'placeholder'}
          </div>
        )
      },
    },
    {
      sort: 'allocated',
      label: 'Total Allocated',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="allocated" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.allocated : 'plchld'}
          </div>
        )
      },
    },
    {
      sort: 'occupied',
      label: (
        <div className="f">
          <ColorMarker color={occupiedColor} />
          Occupied
        </div>
      ),
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="occupied" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.occupied : 'plchld'}
          </div>
        )
      },
    },
    {
      sort: 'unoccupied',
      label: (
        <div className="f">
          <ColorMarker color={unoccupiedColor} />
          Unoccupied
        </div>
      ),
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="unoccupied" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.unoccupied : 'plchld'}
          </div>
        )
      },
    },
    {
      label: '',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell || cell.data.id === 'total') return null

        return (
          <div data-test="unoccupied-tooltip">
            <Tooltip2
              content={
                <DepartmentTooltipContent
                  department={cell.data}
                  occupiedColor={occupiedColor}
                  unoccupiedColor={unoccupiedColor}
                />
              }
            >
              <SimpleBar
                val={cell.data.occupied}
                val2={cell.data.occupied + cell.data.unoccupied}
                max={max}
                barColor={occupiedColor}
                bar2Color={unoccupiedColor}
              />
            </Tooltip2>
          </div>
        )
      },
    },
  ]

  const formatDepartments = (items: IPortfolioDepartment[]) => {
    return items.map((d) => ({
      data: { id: d.department?.id, ...d },
      children:
        d.teams.length > 1
          ? d.teams.map((t: IPortfolioDepartment) => ({
              data: { id: t.department?.id, ...t },
            }))
          : [],
      expandToggle:
        d.teams.length > 1 ? `${d.teams.length} ${pluralize(d.teams.length, 'department')}` : null,
    }))
  }

  let formatedDepartments = formatDepartments(
    orderBy(departments, sort, sortAscending ? 'asc' : 'desc') as IPortfolioDepartment[]
  )

  //todo use table total line feature (appendData, appendColumn)
  formatedDepartments = [
    ...formatedDepartments,
    ...(object
      ? [
          {
            data: {
              id: 'total',
              allocated: object.allocated,
              areaSqft: object.areaSqft,
              areaSqrm: object.areaSqft,
              occupied: object.occupied,
              seatedCount: object.seatedCount,
              unoccupied: object.unoccupied,
              department: {
                id: 'total',
                name: 'Total',
                color: '',
              },
              teams: [],
            },
            children: [],
            expandToggle: '',
          },
        ]
      : []),
  ]

  return (
    <>
      <PrintTitle>
        <h3>Seats By Department</h3>
      </PrintTitle>
      <NewGridTable<IPortfolioDepartment & { id: string }>
        status={loading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        minCellWidth="auto"
        columns={columns}
        data={formatedDepartments}
        sort={{
          column: sort,
          order: sortAscending ? Order.ASC : Order.DESC,
          onSort: (column: string) => {
            if (sort === column) {
              return setSortAscending(!sortAscending)
            } else {
              setSort(column as Sorts)
              setSortAscending(false)
            }
          },
        }}
      />
    </>
  )
}

export function DepartmentTooltipContent({
  department,
  occupiedColor,
  unoccupiedColor,
}: {
  department: IPortfolioDepartment | ITooltipByAllocatedInDepartments
  occupiedColor: string
  unoccupiedColor: string
}) {
  return (
    <div>
      <h5 style={{ margin: '0 0 10px' }}>{department.department?.name}</h5>
      <div className="f">
        <ColorMarker color={occupiedColor} /> Occupied: {department.occupied}
      </div>
      <div
        className="f"
        style={{
          borderBottom: '1px solid white',
          paddingBottom: 3,
          marginBottom: 3,
        }}
      >
        <ColorMarker color={unoccupiedColor} /> Unoccupied: {department.unoccupied}
      </div>
      <div>Total Allocated: {department.allocated}</div>
    </div>
  )
}
